import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AngularFireModule } from '@angular/fire/compat';
import { Subscription } from 'rxjs';

import { StableTextDirective, StableTextService } from '@app/directives/stable-text';
import { hexToRgba } from '@app/shared/color';
import { Message } from '@app/shared/models/app.model';
import { SubtitleBoxSettings } from '@app/services/websocket.service';
import { TextDirectionDirective } from '@app/shared/directives/text-direction.directive';

import { ViewportMonitorService } from '../viewport-monitor.service';

@Component({
  selector: 'app-subtitle-box',
  standalone: true,
  imports: [CdkDrag, AngularFireModule, StableTextDirective, TextDirectionDirective],
  templateUrl: './subtitle-box.component.html',
  styleUrls: ['./subtitle-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ])
  ]
})
export class SubtitleBoxComponent implements OnInit, OnDestroy {
  /** Input stream of messages to display */
  readonly messages = input.required<Message[]>();
  readonly subtitleBoxSettings = input.required<SubtitleBoxSettings>();
  readonly enableEdit = input(false);
  readonly transformScale = input<number>(1);
  readonly dragBoundary = input<HTMLElement | null>(null);
  readonly transcriptLanguage = input<string | null>(null);
  /** Whether to apply translation delay buffering */
  readonly applyTranslationDelay = input(true);
  /** Emits when subtitle box settings are changed */
  @Output() onSubtitleBoxChange = new EventEmitter<SubtitleBoxSettings>();

  readonly subtitleBox = viewChild<ElementRef>('subtitleBox');
  readonly heightCalculator = viewChild<ElementRef>('heightCalculator');

  // Drag and resize state
  dragPosition: WritableSignal<{ x: number; y: number }> = signal({ x: 0, y: 0 });
  readonly isResizing = signal(false);
  readonly isDragging = signal(false);
  width = signal(0);
  containerHeight = signal(0);

  scaledWidth = computed(() => Math.round(this.width() * this.transformScale()));
  scaledHeight = computed(() => Math.round(this.containerHeight()));
  scaledPosition = computed(() => {
    const pos = this.dragPosition();
    return {
      x: Math.round(pos.x * this.transformScale()),
      y: Math.round(pos.y * this.transformScale())
    };
  });


  /** Current viewport dimensions signal for reactive updates */
  private viewportDimensions = signal({ width: window.innerWidth, height: window.innerHeight, scale: 1 });

  /** Scale factor based on viewport size for responsive scaling */
  viewportScale = computed(() => {
    return this.transformScale();
  });

  /** Messages ready for display — delegates to service which handles delay filtering and holdback. */
  displayedMessages = computed(() => {
    if (this.applyTranslationDelay()) {
      return this.stableTextService.displayReadyMessages();
    }
    return this.stableTextService.retainedMessages();
  });

  displayedMessageGroups = computed(() => {
    const messages = this.displayedMessages();
    return messages.map(msg => ({ id: msg.documentID, messages: [msg] }));
  });

  fontSize = computed(() => {
    const baseSize = this.subtitleBoxSettings().font_size;
    const scale = this.transformScale();
    const scaledSize = Math.round(baseSize * scale);
    return `${scaledSize}px`;
  });

  lineHeight = computed(() => {
    const baseSize = this.subtitleBoxSettings().font_size;
    const scale = this.transformScale();
    const scaledSize = Math.round(baseSize * scale);
    return Math.ceil(scaledSize * 1.4);
  });

  hasMessages = computed(() => {
    const messages = this.messages();
    return messages && messages.length > 0;
  });

  heightCalculatorLines = computed(() => {
    const maxLines = this.subtitleBoxSettings().max_visible_lines || 2;
    return Array.from({ length: maxLines }, (_, i) => i + 1);
  });


  overlayBackgroundColor = computed(() => {
    const settings = this.subtitleBoxSettings();
    return hexToRgba(settings.overlay_color, settings.overlay_color_opacity);
  });

  fontColor = computed(() => {
    const settings = this.subtitleBoxSettings();
    return hexToRgba(settings.font_color, settings.font_color_opacity);
  });

  fontFamily = computed(() => {
    const settings = this.subtitleBoxSettings();
    return settings.font_family || 'Arial, sans-serif';
  });

  relativePosition = computed(() => {
    const position = this.subtitleBoxSettings().position.split('x');
    return { x: +position[0], y: +position[1] };
  });

  relativeSize = computed(() => {
    const size = this.subtitleBoxSettings().size.split('x');
    return { width: +size[0], height: +size[1] };
  });

  hasFixedMargins = computed(() => {
    const config = this.subtitleBoxSettings();
    const leftMargin = config.left_margin_px ?? 0;
    const rightMargin = config.right_margin_px ?? 0;
    return leftMargin > 0 || rightMargin > 0;
  });

  hasHorizontalMargins = computed(() => {
    const config = this.subtitleBoxSettings();
    return (config.left_margin_px ?? 0) > 0 || (config.right_margin_px ?? 0) > 0;
  });

  textStrokeColor = computed(() => {
    const config = this.subtitleBoxSettings();
    return config.text_stroke_color ?? '#000000';
  });

  textStrokeWidth = computed(() => {
    const config = this.subtitleBoxSettings();
    return config.text_stroke_width ?? 0;
  });

  textStrokeShadow = computed(() => {
    const width = this.textStrokeWidth();
    const color = this.textStrokeColor();
    const scale = this.transformScale();
    const scaledWidth = width * scale;

    const shadows: string[] = ['2px 2px 4px rgba(0, 0, 0, 0.8)'];

    if (scaledWidth >= 0.5) {
      const steps = 8;
      for (let i = 0; i < steps; i++) {
        const angle = (i * 2 * Math.PI) / steps;
        const x = Math.cos(angle) * scaledWidth;
        const y = Math.sin(angle) * scaledWidth;
        shadows.push(`${x.toFixed(2)}px ${y.toFixed(2)}px 0px ${color}`);
      }
    }

    return shadows.join(', ');
  });

  // Private state
  private startX = 0;
  private startWidth = 0;
  private pendingResizeSettings: SubtitleBoxSettings | null = null;
  private listenerFunctions: Array<Function> = [];
  private viewportSubscription: Subscription | null = null;

  constructor(
    private renderer: Renderer2,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    private viewportMonitor: ViewportMonitorService,
    private stableTextService: StableTextService
  ) {
    // Pass messages to service - it handles retention and sync
    toObservable(this.messages).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(msgs => {
      this.stableTextService.setMessages(msgs);
    });

    // Recalculate when settings change
    toObservable(this.subtitleBoxSettings).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.updateAbsoluteDimensions();
      this.updateAbsolutePosition();

      // Defer height calculation to ensure DOM is updated after change detection
      requestAnimationFrame(() => {
        this.calculateContainerHeight();
      });
      this.cdr.markForCheck();
    });

    // Also recalculate when messages change
    toObservable(this.messages).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Defer height calculation to ensure DOM is updated
      requestAnimationFrame(() => {
        this.calculateContainerHeight();
      });
      this.cdr.markForCheck();
    });
  }

  private calculateContainerHeight(): void {
    const calculatorRef = this.heightCalculator();
    if (!calculatorRef || !calculatorRef.nativeElement) {
      const settings = this.subtitleBoxSettings();
      const maxLines = settings.max_visible_lines || 2;
      const fontSize = settings.font_size;
      const scale = this.transformScale();
      const scaledFontSize = fontSize * scale;
      const fallbackLineHeight = scaledFontSize * 1.4;
      const height = maxLines * fallbackLineHeight;
      this.containerHeight.set(Math.round(height));
      return;
    }

    const calculatedHeight = calculatorRef.nativeElement.offsetHeight;
    this.containerHeight.set(calculatedHeight);
  }

  ngOnInit(): void {
    this.updateAbsolutePosition();
    this.updateAbsoluteDimensions();

    this.setupViewportChangeDetection();

    if (this.enableEdit()) {
      this.listenerFunctions.push(this.renderer.listen('window', 'mousemove', this.onMouseMove.bind(this)));
      this.listenerFunctions.push(this.renderer.listen('window', 'mouseup', this.onMouseUp.bind(this)));
    }
  }

  private setupViewportChangeDetection(): void {
    this.viewportSubscription = this.viewportMonitor.viewport$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(dimensions => {
      this.viewportDimensions.set({
        width: dimensions.width,
        height: dimensions.height,
        scale: dimensions.scale
      });
      this.updateAbsolutePosition();
      this.updateAbsoluteDimensions();
      this.calculateContainerHeight();
      this.cdr.markForCheck();
    });
  }



  ngOnDestroy(): void {
    this.listenerFunctions.forEach(listener => listener());

    // Clean up viewport subscription
    if (this.viewportSubscription) {
      this.viewportSubscription.unsubscribe();
      this.viewportSubscription = null;
    }

  }

  onDragStarted(): void {
    this.isDragging.set(true);
  }

  onDragEnded(event: CdkDragEnd): void {
    if (!this.enableEdit()) {
      console.warn('Editing is disabled. Dragging is not allowed.');
      return;
    }

    this.isDragging.set(false);

    const { x, y } = event.source.getFreeDragPosition();
    const scale = this.transformScale();

    const newPosition = {
      x: x / scale,
      y: y / scale
    };
    this.dragPosition.set(newPosition);

    const viewport = this.viewportDimensions();
    const viewportWidth = viewport.width;
    const viewportHeight = viewport.height;

    const updatedSettings = {
      ...this.subtitleBoxSettings(),
      position: `${newPosition.x / viewportWidth}x${newPosition.y / viewportHeight}`
    };
    this.onSubtitleBoxChange.emit(updatedSettings);
  }

  onMouseDown(event: MouseEvent): void {
    // Prevent resize when margins control the width
    if (this.hasFixedMargins()) {
      return;
    }

    event.preventDefault();
    this.isResizing.set(true);
    this.startX = event.clientX;
    this.startWidth = this.relativeSize().width;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isResizing()) return;

    const dx = event.clientX - this.startX;
    const scale = this.transformScale();
    const viewport = this.viewportDimensions();
    const viewportWidth = viewport.width;
    const dxInTargetSpace = dx / scale;

    // Calculate new relative dimensions
    let newRelativeWidth = this.startWidth + dxInTargetSpace / viewportWidth;

    // Define minimum and maximum relative sizes
    const minRelativeWidth = 0.2; // 20% of viewport width
    const settings = this.subtitleBoxSettings();
    const relativePos = this.relativePosition();
    const maxRelativeWidth = 1 - relativePos.x; // Prevent overflow

    // Apply constraints
    newRelativeWidth = Math.max(minRelativeWidth, Math.min(newRelativeWidth, maxRelativeWidth));

    // Create updated settings with new width
    const currentSize = this.relativeSize();
    const updatedSettings = {
      ...settings,
      size: `${newRelativeWidth}x${currentSize.height}`
    };

    this.pendingResizeSettings = updatedSettings;

    this.width.set(newRelativeWidth * viewportWidth);
  }

  onMouseUp(event: MouseEvent): void {
    this.isResizing.set(false);

    if (this.pendingResizeSettings) {
      this.onSubtitleBoxChange.emit(this.pendingResizeSettings);
      this.pendingResizeSettings = null;
    }
  }

  private updateAbsolutePosition(): void {
    const config = this.subtitleBoxSettings();
    const leftMargin = config.left_margin_px ?? 0;
    const rightMargin = config.right_margin_px ?? 0;
    const relPos = this.relativePosition();

    const viewport = this.viewportDimensions();
    const containerWidth = viewport.width;
    const containerHeight = viewport.height;
    const boxWidth = this.width();

    // X POSITION PRIORITY:
    // 1. Left margin (pin to left edge)
    // 2. Right margin (pin to right edge)
    // 3. Relative position (percentage-based)
    let xPosition: number;
    if (leftMargin > 0) {
      xPosition = leftMargin;
    } else if (rightMargin > 0) {
      xPosition = containerWidth - rightMargin - boxWidth;
    } else {
      xPosition = relPos.x * containerWidth;
    }

    const yPosition = relPos.y * containerHeight;
    this.dragPosition.set({ x: xPosition, y: yPosition });
  }

  private updateAbsoluteDimensions(): void {
    const config = this.subtitleBoxSettings();
    const leftMargin = config.left_margin_px ?? 0;
    const rightMargin = config.right_margin_px ?? 0;

    const viewport = this.viewportDimensions();
    const containerWidth = viewport.width;

    let calculatedWidth: number;

    // PRIORITY 1: If margins are set, calculate width from margins
    if (leftMargin > 0 || rightMargin > 0) {
      calculatedWidth = containerWidth - leftMargin - rightMargin;
    } else {
      // PRIORITY 2: Otherwise use relative width from size field
      const relSize = this.relativeSize();
      calculatedWidth = relSize.width * containerWidth;
    }

    this.width.set(calculatedWidth);
  }
}
